<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { IconCalendar } from '@tabler/icons-vue'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    ServicesService,
    CalendarsService,
    type TService,
    type TCalendar
  } from '@/types/generated'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectService: 'Select service...',
      selectCalendar: 'Select calendar...',
      selectLocationType: 'Select location type...',
      noServicesFound: 'No services found',
      noCalendarsFound: 'No calendars found',
      noLocationTypesFound: 'No location types found',
      sendText: 'Send appointment booking link for',
      withCalendar: 'with calendar',
      asLocationType: 'as',
      locationOnline: 'Online appointment',
      locationTelephone: 'Phone appointment',
      locationInPerson: 'In-person appointment',
      toTheClient: 'to the client.',
      appointmentLinkSent: 'The appointment booking link for',
      wasSentToClient: 'was sent to the client.'
    },
    de: {
      selectService: 'Service auswählen...',
      selectCalendar: 'Kalender auswählen...',
      selectLocationType: 'Terminart auswählen...',
      noServicesFound: 'Keine Services gefunden',
      noCalendarsFound: 'Keine Kalender gefunden',
      noLocationTypesFound: 'Keine Terminarten gefunden',
      sendText: 'Sende Terminbuchungslink für',
      withCalendar: 'mit Kalender',
      asLocationType: 'als',
      locationOnline: 'Online-Termin',
      locationTelephone: 'Telefon-Termin',
      locationInPerson: 'Termin vor Ort',
      toTheClient: 'an den Mandanten.',
      appointmentLinkSent: 'Der Terminbuchungslink für',
      wasSentToClient: 'wurde an den Mandanten gesendet.'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Reactive state
  const isLoadingServices = ref(false)
  const isLoadingCalendars = ref(false)
  const selectedServiceUuid = ref<string>('')
  const selectedCalendarUuid = ref<string>('')
  const selectedLocationType = ref<string>('')
  const services = ref<TService[]>([])
  const calendars = ref<TCalendar[]>([])

  // Computed properties
  const serviceOptions = computed<Option[]>(() => {
    return services.value.map((service) => ({
      value: service.uuid || '',
      title: service.name || 'Untitled Service',
      icon: undefined
    }))
  })

  const calendarOptions = computed<Option[]>(() => {
    return calendars.value.map((calendar) => ({
      value: calendar.uuid || '',
      title: calendar.name || 'Untitled Calendar',
      icon: undefined
    }))
  })

  // Predefined location type options
  const locationTypeOptions = computed<Option[]>(() => {
    return [
      { value: 'ONLINE', title: t('locationOnline') },
      { value: 'TELEPHONE', title: t('locationTelephone') },
      { value: 'IN_PERSON', title: t('locationInPerson') }
    ]
  })

  const selectedService = computed(() => {
    return services.value.find((service) => service.uuid === selectedServiceUuid.value)
  })

  const selectedCalendar = computed(() => {
    return calendars.value.find((calendar) => calendar.uuid === selectedCalendarUuid.value)
  })

  const selectedLocationTypeLabel = computed(() => {
    const option = locationTypeOptions.value.find((opt) => opt.value === selectedLocationType.value)
    return option?.title || selectedLocationType.value
  })

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false &&
        selectedServiceUuid.value &&
        selectedCalendarUuid.value &&
        selectedLocationType.value)
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedService.value) return ''

    const serviceName = selectedService.value.name || 'Untitled Service'
    return `${t('appointmentLinkSent')} "${serviceName}" ${t('wasSentToClient')}`
  })

  // Methods
  const fetchServices = async () => {
    isLoadingServices.value = true
    try {
      const response = await ServicesService.servicesList({
        limit: 100
      })
      services.value = response.results || []
    } catch (error) {
      console.error('Error fetching services:', error)
      services.value = []
    } finally {
      isLoadingServices.value = false
    }
  }

  const fetchCalendars = async () => {
    isLoadingCalendars.value = true
    try {
      const response = await CalendarsService.calendarsList({
        limit: 100
      })
      calendars.value = response.results || []
    } catch (error) {
      console.error('Error fetching calendars:', error)
      calendars.value = []
    } finally {
      isLoadingCalendars.value = false
    }
  }

  const handleServiceSelection = (serviceUuid: string) => {
    selectedServiceUuid.value = serviceUuid
    // Reset dependent selections when service changes
    selectedCalendarUuid.value = ''
    emitPayloadChange()
  }

  const handleCalendarSelection = (calendarUuid: string) => {
    selectedCalendarUuid.value = calendarUuid
    emitPayloadChange()
  }

  const handleLocationTypeSelection = (locationType: string) => {
    selectedLocationType.value = locationType
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        service_uuid: selectedServiceUuid.value ? parseInt(selectedServiceUuid.value) : undefined,
        calendar_uuid: selectedCalendarUuid.value
          ? parseInt(selectedCalendarUuid.value)
          : undefined,
        location_type: selectedLocationType.value,
        start_datetime_iso: props.action.payload.start_datetime_iso || '',
        message: props.action.payload.message || ''
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: any) => {
    if (!payload) return

    if (payload.service_uuid) {
      selectedServiceUuid.value = payload.service_uuid.toString()
    }
    if (payload.calendar_uuid) {
      selectedCalendarUuid.value = payload.calendar_uuid.toString()
    }
    if (payload.location_type) {
      selectedLocationType.value = payload.location_type
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch available data
    await Promise.all([fetchServices(), fetchCalendars()])

    // Emit initial payload if any values are set
    if (selectedServiceUuid.value && selectedCalendarUuid.value && selectedLocationType.value) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('sendText') }}</span>

        <!-- Service Selection -->
        <template v-if="isLoadingServices">
          <SkeletonLoaderInlineField
            width="160px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedServiceUuid"
            variant="inline-text"
            :options="serviceOptions"
            :is-filterable="false"
            :disabled="isLoadingServices"
            :placeholder="serviceOptions.length === 0 ? t('noServicesFound') : t('selectService')"
            name="service"
            @update:model-value="handleServiceSelection"
          />
        </template>

        <span>{{ t('withCalendar') }}</span>

        <!-- Calendar Selection -->
        <template v-if="isLoadingCalendars">
          <SkeletonLoaderInlineField
            width="160px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedCalendarUuid"
            variant="inline-text"
            :options="calendarOptions"
            :is-filterable="false"
            :disabled="isLoadingCalendars || !selectedServiceUuid"
            :placeholder="
              calendarOptions.length === 0 ? t('noCalendarsFound') : t('selectCalendar')
            "
            name="calendar"
            @update:model-value="handleCalendarSelection"
          />
        </template>

        <span>{{ t('asLocationType') }}</span>

        <!-- Location Type Selection -->
        <SelectField
          v-model="selectedLocationType"
          variant="inline-text"
          :options="locationTypeOptions"
          :is-filterable="false"
          :placeholder="t('selectLocationType')"
          name="locationType"
          @update:model-value="handleLocationTypeSelection"
        />

        <span>{{ t('toTheClient') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
